import React, { useCallback, useMemo } from 'react';
import { DOCUMENT_DESCRIPTIONS, TDocumentTypeFile } from 'shared/constants/docs';
import { DOC_TYPES_FILES } from 'shared/constants/pouchDB';
import { IconDelete } from 'shared/resources/svg/IconDelete';
import { TSelectedFile } from 'shared/utils/files/files';
import { Box, Button, Checkbox, IconButton, Typography } from '@mui/material';
import FileTypeSelect from './file-type-select';

type TProjectCreateFilesProps = {
	files: TSelectedFile[];
	handleSelectFile: (file: TSelectedFile) => void;
	handleAddFiles: (newFiles: TSelectedFile[]) => void;
	handleDeleteFiles: (files: TSelectedFile[]) => void;
	showFiles: boolean;
	disabled?: boolean;
	defaultType: TDocumentTypeFile;
	setDefaultType: (type: TDocumentTypeFile) => void;
};

export default function ProjectCreateFiles(props: TProjectCreateFilesProps): JSX.Element {
	const {
		files,
		defaultType,
		setDefaultType,
		showFiles,
		handleSelectFile,
		handleAddFiles,
		handleDeleteFiles,
		disabled = false,
	} = props;

	const handleConfirm = useCallback(() => {
		const curFiles = files.filter((fl) => fl.selected);
		handleDeleteFiles(curFiles);
		handleAddFiles(
			curFiles.map((f) => ({ ...f, type: defaultType, typeConfirmed: true, selected: false }))
		);
	}, [files, defaultType, handleDeleteFiles, handleAddFiles]);

	const unconfirmedFiles = useMemo(() => files.filter((fl) => fl.typeConfirmed !== true), [files]);

	return (
		<>
			{!!unconfirmedFiles.length && (
				<Box borderBottom="1px solid #ddd">
					<Box display="flex" mb={2} alignItems="baseline">
						<Typography alignItems="baseline" color="secondary">
							The following files will be added to the
							<FileTypeSelect value={defaultType} setValue={setDefaultType} />
							folder
						</Typography>
						<Button
							disabled={files.filter((f) => f.selected).length === 0 || disabled}
							onClick={handleConfirm}
							sx={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
							size="small"
						>
							Add to Folder
						</Button>
					</Box>
					<Box display="flex" flexDirection="column" pb={2} rowGap={1}>
						{unconfirmedFiles.map((fl, ind) => (
							<Box key={`${fl.file.name}-${ind}`} display="flex" alignItems="center">
								<Typography flex={1}>{fl.file.name}</Typography>
								<Checkbox checked={fl.selected} onChange={() => handleSelectFile(fl)} />
								<IconButton
									size="small"
									onClick={() => handleDeleteFiles([fl])}
									disabled={disabled}
								>
									<IconDelete />
								</IconButton>
							</Box>
						))}
					</Box>
				</Box>
			)}
			{showFiles &&
				DOC_TYPES_FILES.map((fileType: TDocumentTypeFile) => (
					<Box
						borderBottom="1px solid #ddd"
						key={fileType}
						sx={{ ':not(:last-child)': { marginBottom: 0 } }}
					>
						<Typography
							textTransform="uppercase"
							fontWeight="bold"
							color="secondary"
							mb={2}
						>{`${DOCUMENT_DESCRIPTIONS[fileType].title}:`}</Typography>
						<Box display="flex" flexDirection="column" pb={2} rowGap={1}>
							{files
								.filter((fl) => fl.type === fileType && fl.typeConfirmed === true)
								.map((fl, ind) => (
									<Box display="flex" mb={1} alignItems="center" key={`${fl.file.name}-${ind}`}>
										<Typography flex={1}>{fl.file.name}</Typography>
										<IconButton
											size="small"
											onClick={() => handleDeleteFiles([fl])}
											disabled={disabled}
										>
											<IconDelete />
										</IconButton>
									</Box>
								))}
						</Box>
					</Box>
				))}
		</>
	);
}
